'use client';

import { IcLikeFilled, LikeMotion, Image as MDSImage, Typography } from '@musinsa/mds';
import { ERROR_CODE_LIKE_UNAUTHORIZED, isApiError } from '@musinsa/services-curator';
import clsx from 'clsx';
import type { FC, KeyboardEventHandler, MouseEventHandler, PropsWithChildren } from 'react';
import { createContext, useContext, useState } from 'react';
import { ProductInformationPrimitive, type PropsWithStyles } from '../../../common';
import { createLoginUrl } from '../../../common/auth/redirectToLogin';
import { useProductLikeMutation } from '../../../common/hooks/useProductLikeMutation';

export type SnapTaggedGoodsCardSize = 's' | 'm';

const SnapTaggedGoodsCardSizeContext = createContext<SnapTaggedGoodsCardSize>('s');

const useSnapTaggedGoodsCardSize = (): SnapTaggedGoodsCardSize =>
  useContext(SnapTaggedGoodsCardSizeContext);

interface ImageProps {
  imageUrl: string;
  isMobile: boolean;
  isSoldOut: boolean;
  goodsNo: string;
  initialIsLiked: boolean;
  invalidateSnapDetail: () => void;
  navigate: (href: string) => void;
}

export type SnapTaggedGoodsCardImageProps = PropsWithStyles<ImageProps>;

const Image: FC<SnapTaggedGoodsCardImageProps> = ({
  imageUrl,
  isMobile,
  isSoldOut,
  goodsNo,
  initialIsLiked,
  invalidateSnapDetail,
  navigate,
  className,
  ...rest
}) => {
  const size = useSnapTaggedGoodsCardSize();
  const [isLiked, setIsLiked] = useState(initialIsLiked);

  const productLikeMutation = useProductLikeMutation({
    onSuccess: () => {
      invalidateSnapDetail();
    },
    onError: (error: unknown, { isLiked: next }) => {
      if (isApiError(error) && error.data?.meta?.errorCode === ERROR_CODE_LIKE_UNAUTHORIZED) {
        const url = createLoginUrl(window.location.href);
        navigate(url);
        return;
      }
      setIsLiked(!next);
    },
  });

  const onClickLike = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    const next = !isLiked;
    setIsLiked(next);
    productLikeMutation.mutate({ goodsNo, isLiked: next });
  };

  const isMedium = size === 'm';
  const imgW = isMedium ? 72 : 48;
  const imgH = isMedium ? 86 : 58;
  const fetchW = isMobile ? (isMedium ? 200 : 150) : isMedium ? 280 : 200;
  const likeIconSize = isMedium ? '18px' : '16px';

  return (
    <div
      className={clsx(
        'relative shrink-0 rounded-[4px] overflow-hidden',
        isMedium ? 'h-[86px] w-[72px]' : 'h-[58px] w-[48px]',
        className,
      )}
      {...rest}
    >
      <MDSImage
        alt=""
        className="size-full object-cover"
        width={imgW}
        height={imgH}
        src={`${imageUrl}${imageUrl.includes('?') ? '&' : '?'}width=${fetchW}&q=75&format=webp`}
      />
      {isSoldOut && (
        <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 py-0.5 text-[11px] text-white">
          품절
        </span>
      )}
      <div
        className={clsx(
          'absolute z-10 w-[16px] h-[16px] flex items-center justify-center',
          isMedium ? 'right-[4px] bottom-[4px]' : 'right-[2px] bottom-[2px]',
        )}
      >
        <LikeMotion like={isLiked}>
          {isLiked ? (
            <IcLikeFilled
              fillColor="red"
              height={likeIconSize}
              strokeColor="transparent"
              width={likeIconSize}
              onClick={onClickLike}
            />
          ) : (
            <IcLikeFilled
              fillColor="gray-500"
              height={likeIconSize}
              opacity={0.6}
              strokeColor="white"
              width={likeIconSize}
              onClick={onClickLike}
            />
          )}
        </LikeMotion>
      </div>
    </div>
  );
};

interface InfoProps {
  brandName: string;
  goodsName: string;
  displayRate: number;
  displayPrice: number;
  isExclusive?: boolean;
}

export type SnapTaggedGoodsCardInfoProps = PropsWithStyles<InfoProps>;

const Info: FC<SnapTaggedGoodsCardInfoProps> = ({
  brandName,
  goodsName,
  displayRate,
  displayPrice,
  isExclusive,
  className,
  ...rest
}) => {
  const size = useSnapTaggedGoodsCardSize();
  const isMedium = size === 'm';

  if (isMedium) {
    return (
      <div className={clsx('flex min-w-0 grow flex-col', className)} {...rest}>
        <Typography variant="body13pxSemi" colorScheme="black">
          {brandName}
        </Typography>
        <ProductInformationPrimitive.GoodsName
          goodsName={goodsName}
          className="pt-[2px]! line-clamp-2"
        />
        <ProductInformationPrimitive.DiscountPriceRow
          finalDiscount={displayRate}
          finalPrice={displayPrice}
          className="pt-[2px]!"
        />
        {isExclusive && (
          <ProductInformationPrimitive.ProductBadgeContainer>
            <ProductInformationPrimitive.ExclusiveGoodsBadge />
          </ProductInformationPrimitive.ProductBadgeContainer>
        )}
      </div>
    );
  }

  return (
    <div className={clsx('flex min-w-0 grow flex-col', className)} {...rest}>
      <ProductInformationPrimitive.BrandName brandName={brandName} />
      <ProductInformationPrimitive.GoodsName
        goodsName={goodsName}
        className="pt-[2px]! line-clamp-1"
      />
      <ProductInformationPrimitive.DiscountPriceRow
        finalDiscount={displayRate}
        finalPrice={displayPrice}
        className="pt-[2px]!"
      />
      {isExclusive && (
        <ProductInformationPrimitive.ProductBadgeContainer>
          <ProductInformationPrimitive.ExclusiveGoodsBadge />
        </ProductInformationPrimitive.ProductBadgeContainer>
      )}
    </div>
  );
};

interface RootProps {
  onClick: MouseEventHandler<HTMLDivElement>;
  onKeyDown: KeyboardEventHandler<HTMLDivElement>;
  size?: SnapTaggedGoodsCardSize;
}

export type SnapTaggedGoodsCardRootProps = PropsWithChildren<PropsWithStyles<RootProps>>;

/** @deprecated Use SnapTaggedGoodsCardRootProps */
export type SnapTaggedGoodsCardContainerProps = SnapTaggedGoodsCardRootProps;

const Root: FC<SnapTaggedGoodsCardRootProps> = ({
  onClick,
  onKeyDown,
  children,
  className,
  size = 's',
  ...rest
}) => (
  <SnapTaggedGoodsCardSizeContext.Provider value={size}>
    <div
      {...rest}
      role="button"
      tabIndex={0}
      className={clsx(
        'flex cursor-pointer items-center overflow-hidden bg-neutral-color-white focus-visible:outline-0',
        size === 's' && 'shrink-0 py-[12px]! px-[8px]! gap-x-[8px]',
        size === 'm' && 'w-full px-[16px]! gap-x-[12px]',
        className,
      )}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  </SnapTaggedGoodsCardSizeContext.Provider>
);

export const SnapTaggedGoodsCard = {
  Root,
  Image,
  Info,
};
